import { Switch, SwitchGroup, SwitchLabel } from '@headlessui/react';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import DeactivateAccountDialog from '../../../components/deactivate-account-dialog/DeactivateAccountDialog';
import UpdateEmailDialog from '../../../components/update-email-dialog/updateEmailDialog';
import UpdateNameDialog from '../../../components/update-name-dialog/updateNameDialog';
import UpdatePasswordDialog from '../../../components/update-password-dialog/updatePasswordDialog';
import UpdateTimezoneDialog from '../../../components/update-timezone-dialog/updateTimezoneDialog';
import { useAccount } from '../../../hooks/useAccount';
import {
  useUpdateEmail,
  useUpdateName,
  useUpdateSetting,
  useUpdateTimezoneSetting,
} from '../../../hooks/useAccountMutations';
import SettingsPageLayout from '../../../layouts/settings/settingsPageLayout';
import {
  AppSetting,
  BooleanSettingValue,
  getBooleanSetting,
} from '../../../services/models/app-settings';
import { classNames } from '../../../utils/class-names';

const OverviewPage: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();

  const { account: currentAccount } = useAccount();
  const account = currentAccount!;
  const { updateName } = useUpdateName();
  const { updateEmail } = useUpdateEmail();
  const { updateSetting } = useUpdateSetting();
  const { updateTimezoneSetting } = useUpdateTimezoneSetting();

  // Preferences
  const timezone =
    account.settings?.length > 0 ? account.settings.find(x => x.name === 'time_zone')?.value : '';
  const [newTab, setNewTab] = useState(false);
  const [featureAnnouncements, setFeatureAnnouncements] = useState(false);

  // Dialogs
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [showUpdateEmailDialog, setShowUpdateEmailDialog] = useState(false);
  const [showUpdatePasswordDialog, setShowUpdatePasswordDialog] = useState(false);
  const [showTimezoneDialog, setShowTimezoneDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  // The /cancel deep link (emails, support) redirects here with ?deactivate;
  // open the dialog and strip the param so a refresh doesn't reopen it.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('deactivate')) {
      searchParams.delete('deactivate');
      setShowDeactivateDialog(true);
      history.replace({ pathname: location.pathname, search: searchParams.toString() });
    }
  }, [location.search, location.pathname, history]);

  useEffect(() => {
    setNewTab(getBooleanSetting(account.settings, AppSetting.NewTab));
    setFeatureAnnouncements(
      getBooleanSetting(account.settings, AppSetting.ReceiveFeatureAnnouncement)
    );
  }, [account.settings]);

  const getTimezoneSetting = () => {
    const setting = account.settings?.find(x => x.name === AppSetting.Timezone);
    return !!setting ? setting.value : '';
  };

  const handleNewTabChange = () => {
    setNewTab(v => !v);
    updateSetting(AppSetting.NewTab, !newTab ? BooleanSettingValue.Yes : BooleanSettingValue.No);
  };

  const handleFeatureAnnouncementsChange = () => {
    setFeatureAnnouncements(v => !v);
    updateSetting(
      AppSetting.ReceiveFeatureAnnouncement,
      !featureAnnouncements ? BooleanSettingValue.Yes : BooleanSettingValue.No
    );
  };

  return (
    <SettingsPageLayout title="Settings Overview | Newslit">
      {/* Profile Settings with inline editing */}
      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Profile</h3>
          <p className="max-w-2xl text-sm text-gray-500 m-0">
            Put a face to your name! Make changes to login details, email, password & preferences.
          </p>
        </div>

        <div className="mt-6">
          <dl className="divide-y divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">
                  {account?.firstName} {account?.lastName}
                </span>
                <span className="ml-4 shrink-0">
                  <button
                    type="button"
                    className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                    onClick={() => {
                      setShowNameDialog(true);
                    }}
                  >
                    Update
                  </button>
                </span>
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
              <dt className="text-sm font-medium text-gray-500">Avatar</dt>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">
                  <img className="h-8 w-8 rounded-full" src={account?.imageUrl} alt="" />
                </span>
                <span className="ml-4 shrink-0 flex items-start space-x-4">
                  <button
                    type="button"
                    className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                    onClick={() => {
                      window.open('https://en.gravatar.com/', '_blank');
                    }}
                  >
                    Update
                  </button>
                </span>
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">
                  {account?.email?.emailAddress}{' '}
                  {!account?.email?.verified && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 capitalize">
                      Needs Verification
                    </span>
                  )}
                </span>
                <span className="ml-4 shrink-0">
                  <button
                    type="button"
                    className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                    onClick={() => {
                      setShowUpdateEmailDialog(true);
                    }}
                  >
                    Update
                  </button>
                </span>
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
              <dt className="text-sm font-medium text-gray-500">Password</dt>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">• • • • • • • •</span>
                <span className="ml-4 shrink-0">
                  <button
                    type="button"
                    className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                    onClick={() => {
                      setShowUpdatePasswordDialog(true);
                    }}
                  >
                    Update
                  </button>
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Preferences Settings*/}
      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Preferences</h3>
          <p className="max-w-2xl text-sm text-gray-500">Manage your account preferences.</p>
        </div>
        <div className="mt-6">
          <dl className="divide-y divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Timezone</dt>

              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">{timezone}</span>
                <span className="ml-4 shrink-0">
                  <button
                    type="button"
                    className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                    onClick={() => {
                      setShowTimezoneDialog(true);
                    }}
                  >
                    Update
                  </button>
                </span>
              </dd>
            </div>
            <SwitchGroup as="div" className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
              <SwitchLabel as="dt" className="text-sm font-medium text-gray-500" passive>
                Open external links in new browser tab?
              </SwitchLabel>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <Switch
                  checked={newTab}
                  onChange={handleNewTabChange}
                  className={classNames(
                    newTab ? 'bg-secondary-dark' : 'bg-gray-200',
                    'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:ml-auto'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      newTab ? 'translate-x-5' : 'translate-x-0',
                      'inline-block h-5 w-5 rounded-full bg-white shadow-sm transform ring-0 transition ease-in-out duration-200'
                    )}
                  />
                </Switch>
              </dd>
            </SwitchGroup>
            <SwitchGroup as="div" className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
              <SwitchLabel as="dt" className="text-sm font-medium text-gray-500" passive>
                Receive feature announcements
              </SwitchLabel>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <Switch
                  checked={featureAnnouncements}
                  onChange={handleFeatureAnnouncementsChange}
                  className={classNames(
                    featureAnnouncements ? 'bg-secondary-dark' : 'bg-gray-200',
                    'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:ml-auto'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      featureAnnouncements ? 'translate-x-5' : 'translate-x-0',
                      'inline-block h-5 w-5 rounded-full bg-white shadow-sm transform ring-0 transition ease-in-out duration-200'
                    )}
                  />
                </Switch>
              </dd>
            </SwitchGroup>
          </dl>
        </div>
      </div>

      {/* Deactivate Account*/}
      <div className="mt-10">
        <div className="space-y-1">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Deactivate your account</h3>
          <p className="max-w-2xl text-sm text-gray-500">
            If you do this, all your Briefs and account data will get removed from our system.
          </p>
        </div>
        <div className="mt-6">
          <button
            type="button"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            onClick={() => {
              setShowDeactivateDialog(true);
            }}
          >
            Deactivate account
          </button>
        </div>
      </div>

      {showNameDialog && (
        <UpdateNameDialog
          isOpen={showNameDialog}
          firstName={account.firstName}
          lastName={account.lastName}
          onClose={() => {
            setShowNameDialog(false);
          }}
          onSave={(firstName, lastName) => updateName(firstName, lastName)}
        />
      )}

      {showUpdateEmailDialog && (
        <UpdateEmailDialog
          isOpen={showUpdateEmailDialog}
          email={account?.email?.emailAddress || ''}
          onClose={() => {
            setShowUpdateEmailDialog(false);
          }}
          onSave={email => updateEmail(email)}
        />
      )}

      {showUpdatePasswordDialog && (
        <UpdatePasswordDialog
          isOpen={showUpdatePasswordDialog}
          onClose={() => {
            setShowUpdatePasswordDialog(false);
          }}
          account={account}
        />
      )}

      {showDeactivateDialog && (
        <DeactivateAccountDialog
          account={account}
          open={showDeactivateDialog}
          onClose={() => {
            setShowDeactivateDialog(false);
          }}
        />
      )}

      {showTimezoneDialog && (
        <UpdateTimezoneDialog
          isOpen={showTimezoneDialog}
          onClose={() => {
            setShowTimezoneDialog(false);
          }}
          timezone={getTimezoneSetting()}
          onSave={timezone => updateTimezoneSetting(timezone)}
        />
      )}
    </SettingsPageLayout>
  );
};

export default OverviewPage;
